import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Users } from '@phosphor-icons/react';
import API from '@/lib/api';
import { fetchPdfActions, openPdf } from '@/lib/pdfActions';
import { useAuth } from '@/contexts/AuthContext';

const PERIODES = [
  { key: 'dag', label: 'Vandaag', icon: '☀️' },
  { key: 'week', label: 'Week (7d)', icon: '📅' },
  { key: '14d', label: '14 dagen', icon: '📆' },
  { key: 'maand', label: 'Maand (30d)', icon: '🗓️' },
  { key: '90d', label: '90 dagen', icon: '📊' },
  { key: '365d', label: 'Jaar', icon: '📈' },
  { key: 'custom', label: 'Eigen datum', icon: '🎯' },
];

const fmt = (n) => `€ ${(parseFloat(n) || 0).toFixed(2).replace('.', ',')}`;

export default function TopKlantenPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const magWinst = user?.role === 'admin' || !!user?.mag_winst_zien;
  const [periode, setPeriode] = useState(() => localStorage.getItem('top-klanten-periode') || 'maand');
  const [van, setVan] = useState('');
  const [tot, setTot] = useState(new Date().toISOString().slice(0, 10));
  const [limit, setLimit] = useState(20);
  const [data, setData] = useState({ items: [], totaal_omzet: 0, totaal_docs: 0, aantal_klanten: 0, van: '', tot: '' });
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState('omzet');

  const load = async () => {
    setBusy(true);
    try {
      const params = { limit };
      if (periode === 'custom') {
        if (!van || !tot) { setBusy(false); return; }
        params.van = van; params.tot = tot;
      } else {
        params.periode = periode;
      }
      const r = await API.get('/dashboard/top-klanten', { params });
      setData(r.data || { items: [], totaal_omzet: 0, totaal_docs: 0, aantal_klanten: 0 });
      try { localStorage.setItem('top-klanten-periode', periode); } catch {}
    } catch (e) {
      alert('Fout bij ophalen: ' + (e.response?.data?.detail || e.message));
    }
    setBusy(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [periode, limit]);
  useEffect(() => { if (periode === 'custom' && van && tot) load(); /* eslint-disable-next-line */ }, [van, tot]);

  const items = useMemo(() => {
    const arr = [...(data.items || [])];
    if (sort === 'docs') arr.sort((a, b) => (b.aantal_docs || 0) - (a.aantal_docs || 0));
    else if (sort === 'winst') arr.sort((a, b) => (b.winst || 0) - (a.winst || 0));
    else if (sort === 'gemiddelde') arr.sort((a, b) => (b.gemiddelde_orderwaarde || 0) - (a.gemiddelde_orderwaarde || 0));
    else arr.sort((a, b) => (b.omzet || 0) - (a.omzet || 0));
    return arr;
  }, [data.items, sort]);

  const maxOmzet = useMemo(() => Math.max(1, ...items.map(i => i.omzet || 0)), [items]);

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-7xl mx-auto" data-testid="top-klanten-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 max-w-full">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users size={28} weight="duotone" className="text-[#002FA7]" />
            Top klanten
          </h1>
          <div className="text-xs text-gray-500 mt-1">
            Beste klanten gerangschikt op omzet · {data.van && `${data.van} → ${data.tot}`}
          </div>
        </div>
        <div className="flex flex-wrap max-w-full gap-2">
          <button
            type="button"
            onClick={async () => {
              try {
                const params = { limit };
                if (periode === 'custom') {
                  if (!van || !tot) { alert('Vul eerst van + tot in'); return; }
                  params.van = van; params.tot = tot;
                } else {
                  params.periode = periode;
                }
                const actions = await fetchPdfActions({
                  endpoint: '/dashboard/top-klanten/pdf',
                  label: 'Top klanten',
                  params,
                });
                openPdf(actions);
              } catch (e) {
                alert('PDF-export mislukt: ' + (e.response?.data?.detail || e.message));
              }
            }}
            className="text-xs px-3 py-2 rounded border-2 border-[#FF6B0D] bg-white text-[#FF6B0D] hover:bg-[#FF6B0D] hover:text-white font-bold inline-flex items-center gap-1.5"
            data-testid="top-klant-pdf"
          >
            🖨️ PDF afdrukken
          </button>
          <Link to="/top-artikels" className="text-xs px-3 py-2 rounded border border-gray-200 bg-white hover:border-orange-300 text-gray-700">📊 Top artikelen</Link>
          <Link to="/dashboard" className="text-xs text-[#002FA7] hover:underline self-center">← Dashboard</Link>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap gap-2 mb-3">
          {PERIODES.map(p => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriode(p.key)}
              className={`px-3 py-2 rounded text-xs font-bold border-2 transition ${
                periode === p.key
                  ? 'bg-[#002FA7] border-[#002FA7] text-white shadow-sm'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300'
              }`}
              data-testid={`top-klant-periode-${p.key}`}
            >
              <span className="mr-1">{p.icon}</span>{p.label}
            </button>
          ))}
        </div>
        {periode === 'custom' && (
          <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-gray-100">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Van</label>
              <input type="date" className="block field-input" value={van} onChange={e => setVan(e.target.value)} data-testid="top-klant-van" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Tot</label>
              <input type="date" className="block field-input" value={tot} onChange={e => setTot(e.target.value)} data-testid="top-klant-tot" />
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-gray-100">
          <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-2">
            Toon top:
            <select className="text-xs font-mono bg-white border border-gray-300 rounded px-2 py-1" value={limit} onChange={e => setLimit(parseInt(e.target.value, 10))} data-testid="top-klant-limit">
              {[10, 20, 30, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <label className="text-[10px] uppercase tracking-wider text-gray-500 font-bold inline-flex flex-wrap max-w-full items-center gap-2">
            Sorteer op:
            <select className="text-xs font-mono bg-white border border-gray-300 rounded px-2 py-1 min-w-0 max-w-full" value={sort} onChange={e => setSort(e.target.value)} data-testid="top-klant-sort">
              <option value="omzet">Omzet</option>
              <option value="docs">Aantal documenten</option>
              <option value="gemiddelde">Gemiddelde bestelling</option>
              {magWinst && <option value="winst">Winst</option>}
            </select>
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Totaal omzet" value={fmt(data.totaal_omzet)} color="green" />
        <StatBox label="Totaal documenten" value={data.totaal_docs} color="blue" />
        <StatBox label="Aantal klanten" value={data.aantal_klanten} color="orange" />
        <StatBox label="Periode" value={data.van || '-'} suffix={data.tot ? `→ ${data.tot}` : ''} color="gray" />
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-[10px] uppercase tracking-wider text-gray-500">
              <th className="text-left p-3 w-12">#</th>
              <th className="text-left p-3">Klant</th>
              <th className="text-right p-3 w-16">Docs</th>
              <th className="text-right p-3 w-28">Omzet</th>
              <th className="text-right p-3 w-20">% omzet</th>
              <th className="text-right p-3 w-28">Gem. order</th>
              {magWinst && <th className="text-right p-3 w-24">Winst</th>}
              {magWinst && <th className="text-right p-3 w-20">Marge</th>}
              <th className="text-right p-3 w-24">Openstaand</th>
              <th className="text-left p-3 w-28">Laatste</th>
            </tr>
          </thead>
          <tbody>
            {busy && (
              <tr><td colSpan={magWinst ? 10 : 8} className="p-6 text-center text-gray-400 text-sm">⏳ Laden...</td></tr>
            )}
            {!busy && items.length === 0 && (
              <tr><td colSpan={magWinst ? 10 : 8} className="p-6 text-center text-gray-400 text-sm italic">Geen klantactiviteit in deze periode.</td></tr>
            )}
            {!busy && items.map((it, i) => {
              const barPct = (it.omzet / maxOmzet) * 100;
              return (
                <tr
                  key={it.klant_id || `kl-${i}`}
                  className={`border-b border-gray-100 hover:bg-blue-50/40 transition cursor-pointer ${i < 3 ? 'bg-yellow-50/40' : ''} ${it.geblokkeerd ? 'opacity-70' : ''}`}
                  onClick={() => it.klant_id && navigate(`/klanten?edit=${it.klant_id}`)}
                  data-testid={`top-klant-rij-${i}`}
                >
                  <td className="p-3 font-bold text-gray-500 text-center">
                    {i === 0 && '🥇'}
                    {i === 1 && '🥈'}
                    {i === 2 && '🥉'}
                    {i > 2 && `${i + 1}`}
                  </td>
                  <td className="p-3">
                    <div className="font-bold text-gray-900 flex items-center gap-2">
                      {it.klant_naam}
                      {it.geblokkeerd && <span className="text-[9px] bg-red-100 text-red-700 px-1 rounded font-mono">🚫 GEBLOKKEERD</span>}
                    </div>
                    <div className="text-[10px] text-gray-500 flex flex-wrap gap-2 mt-0.5">
                      {it.gemeente && <span>📍 {it.gemeente}</span>}
                      {it.email && <span>✉️ {it.email}</span>}
                      {it.tel && <span>☎️ {it.tel}</span>}
                      {Object.entries(it.doc_types || {}).map(([t, n]) => (
                        <span key={t} className="bg-gray-100 px-1 rounded">{t}: {n}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 text-right font-bold text-gray-700">{it.aantal_docs}</td>
                  <td className="p-3 text-right">
                    <div className="font-mono font-bold">{fmt(it.omzet)}</div>
                    <div className="h-1 bg-gray-100 rounded mt-1 overflow-hidden">
                      <div className="h-full bg-[#002FA7]" style={{ width: `${barPct}%` }} />
                    </div>
                  </td>
                  <td className="p-3 text-right font-mono text-xs text-blue-700 font-bold">{it.pct_omzet}%</td>
                  <td className="p-3 text-right font-mono text-gray-700">{fmt(it.gemiddelde_orderwaarde)}</td>
                  {magWinst && (
                    <td className={`p-3 text-right font-mono ${it.winst === null ? 'text-gray-300' : it.winst < 0 ? 'text-red-600 font-bold' : 'font-bold'}`}>
                      {it.winst === null ? '—' : fmt(it.winst)}
                    </td>
                  )}
                  {magWinst && (
                    <td className={`p-3 text-right font-mono text-xs ${it.marge_pct === null ? 'text-gray-300' : it.marge_pct < 20 ? 'text-amber-700' : 'text-gray-600'}`}>
                      {it.marge_pct === null ? '—' : `${it.marge_pct}%`}
                    </td>
                  )}
                  <td className={`p-3 text-right font-mono text-xs ${(it.openstaand || 0) > 0 ? 'text-amber-700 font-bold' : 'text-gray-400'}`}>
                    {(it.openstaand || 0) > 0 ? fmt(it.openstaand) : '—'}
                  </td>
                  <td className="p-3 text-left text-xs text-gray-500">{it.laatste || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-gray-400 text-center pt-2">
        💡 Klik op een rij om de klantfiche te openen. Top 3 staan met goud/zilver/brons aangeduid.
      </div>
    </div>
  );
}

function StatBox({ label, value, suffix, color = 'blue' }) {
  const colors = {
    blue: 'border-blue-200 bg-blue-50',
    green: 'border-green-200 bg-green-50',
    orange: 'border-orange-200 bg-orange-50',
    gray: 'border-gray-200 bg-gray-50',
  };
  return (
    <div className={`rounded border-2 ${colors[color]} p-3`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
      <div className="text-lg font-bold text-gray-900 truncate">{value}</div>
      {suffix && <div className="text-[10px] text-gray-500">{suffix}</div>}
    </div>
  );
}
